/**
 * Easter eggs: the words on the city's billboards and neon signs, rare road messages, loading-screen tips and the
 * crash screen's Excel × Solana jokes. Content only — `src/world/easterEggs.ts` draws the signs; GameApp may show the
 * tips and jokes (wiring: EASTER_EGGS.md). The chaser is ELON — you stole his car, he wants it back: friendly parody
 * only (no Tesla/SpaceX names or logos, no likeness, never the word "Roadster"). Every fact here was checked on 2026-10-16; the source is next to it.
 * Rules: Solana culture, friendly, no token shilling, no prices or financial claims, no real people's likeness, no other
 * companies' logos (names as plain words only). A fact that goes stale gets `until` (last day it may show, local time).
 */

export type SignStyle = "board" | "neon" | "sheet";

export interface SignSpec {
  id: string;
  /** board = a lit billboard; neon = a dark sign with glowing letters; sheet = a spreadsheet (Solana on Excel) */
  style: SignStyle;
  /** first line big, the rest smaller (see `big`) */
  lines: string[];
  /** which lines get the big size (default: the middle of three, else the first; the second after "…=" or "…,") */
  big?: number[];
  /** background, ink (main text), accent (second lines, stripes, neon tubes) — CSS colours */
  bg: string;
  ink: string;
  accent: string;
  /** relative chance each time a sign is put up (the Vibhu code is a regular until the conference — Karolis 2026-10-16) */
  weight: number;
  /** a mark drawn centred on a board with the words underneath (a file in public/, e.g. "icons/sb-mark-white.svg"); clean: no wedge, no bars */
  logo?: string;
  /** last day (YYYY-MM-DD) the sign may show: time-limited facts disappear on their own */
  until?: string;
  /** where the words come from (not shown) */
  source?: string;
}

const PURPLE = "#9945FF";
const GREEN = "#14F195";
const INK = "#191819";
const CREAM = "#F4F1FA";

export const SIGNS: SignSpec[] = [
  { id: "bp26", style: "board", lines: ["BREAKPOINT 2026", "LONDON · OLYMPIA · 15–17 NOV"], bg: INK, ink: "#FFFFFF", accent: GREEN, weight: 3, until: "2026-11-17",
    source: "https://solana.com/breakpoint — 'Breakpoint 2026: Solana Conference', 15–17 November 2026, Olympia, London" },
  { id: "bp26-neon", style: "neon", lines: ["SEE YOU AT", "BREAKPOINT", "LONDON 2026"], bg: "#120f22", ink: GREEN, accent: PURPLE, weight: 1.5, until: "2026-11-17",
    source: "https://solana.com/breakpoint" },
  { id: "vibhu", style: "board", lines: ["BREAKPOINT 2026", "BP26-VIBHU", "25% OFF TICKETS"], bg: "#FFD43B", ink: INK, accent: PURPLE, weight: 1.5, until: "2026-11-17",
    source: "https://www.bp26-vibhu.com/memo ('Made by Vibhu': BP26-VIBHU, 25% off GA, $550 → $412.50); https://breakpointmlm.com ('A parody by @vibhu' … 'Breakpoint 2026 tickets and the BP26 prizes are real')" },
  { id: "gm", style: "neon", lines: ["gm"], bg: "#120f22", ink: GREEN, accent: PURPLE, weight: 1.2, source: "crypto-wide greeting; Solana culture staple" },
  { id: "opos", style: "board", big: [0, 1], lines: ["ONLY POSSIBLE", "ON SOLANA"], bg: PURPLE, ink: "#FFFFFF", accent: GREEN, weight: 1,
    source: "OPOS — community motto + 2023 'Only Possible on Solana' hackathon (LamportDAO): solanacompass.com/learn/Solfate/only-possible-on-solana-product-ideas-and-hackathon-feat-mert-from-helius-solfate-podcast-30" },
  { id: "slots", style: "neon", lines: ["200 MS SLOTS", "WAS 400 MS · LIVE SINCE 9 OCT 2026"], bg: "#120f22", ink: "#FFFFFF", accent: GREEN, weight: 1,
    source: "https://solana.com/upgrades/reduced-slot-times — SIMD-0525, 400 → 200 ms, last step epoch 1053, 9 Oct 2026, 'Live on Mainnet'" },
  { id: "poh", style: "board", lines: ["PROOF OF HISTORY", "TIME, VERIFIED"], bg: CREAM, ink: INK, accent: PURPLE, weight: 1,
    source: "https://solana.com/docs/terminology — PoH: 'a stack of proofs, each of which proves that some data existed before the proof was created'" },
  { id: "lamports", style: "board", lines: ["1 SOL =", "1,000,000,000 LAMPORTS"], bg: GREEN, ink: INK, accent: INK, weight: 0.8,
    source: "https://solana.com/docs/terminology — lamport: 0.000000001 SOL" },
  { id: "glass", style: "neon", lines: ["CHEWING GLASS", "WHAT SOLANA DEVS DO"], bg: "#120f22", ink: "#FF5FA2", accent: GREEN, weight: 1,
    source: "Solana's podcast series 'Chewing Glass' (for devs, by devs): https://solana.com/validated/episodes/chewing-glass-paulx-ep-1" },
  { id: "summer", style: "board", lines: ["SOLANA SUMMER", "EST. 2021"], bg: "#FF9F1C", ink: INK, accent: "#FFFFFF", weight: 0.8,
    source: "Solana Foundation, 'Happy Solstice 2021: celebrating Solana's sunny year': https://solana.com/news/happy-solstice-2021-celebrating-solanas-sunny-year" },
  { id: "alpenglow", style: "neon", lines: ["ALPENGLOW", "~150 MS FINALITY TARGET"], bg: "#120f22", ink: "#7FD8FF", accent: PURPLE, weight: 0.8,
    source: "https://solana.com/upgrades/alpenglow — 'targets roughly 150 milliseconds'; mainnet expected epoch 1064, 21 Oct 2026" },
  { id: "sealevel", style: "board", lines: ["SEALEVEL", "THE PARALLEL RUNTIME"], bg: "#2D6CDF", ink: "#FFFFFF", accent: GREEN, weight: 0.6,
    source: "https://solana.com/docs/terminology — Sealevel: 'Solana's parallel run-time for onchain programs'" },
  { id: "firedancer", style: "neon", lines: ["FIREDANCER", "OPEN ALL NIGHT"], bg: "#120f22", ink: "#FF7A1A", accent: "#FFD43B", weight: 0.6,
    source: "pun on the Firedancer validator client (Frankendancer on mainnet 2024): https://www.theblock.co/post/317490/jump-frankendancer-launches-on-solana-mainnet" },
  { id: "lfg", style: "neon", lines: ["LFG"], bg: "#120f22", ink: PURPLE, accent: GREEN, weight: 0.8, source: "crypto slang" },
  { id: "nothing", style: "board", big: [0, 1], lines: ["PROBABLY", "NOTHING"], bg: CREAM, ink: INK, accent: GREEN, weight: 0.7, source: "crypto slang" },
  { id: "pay", style: "neon", lines: ["SOLANA PAY", "ACCEPTED HERE"], bg: "#120f22", ink: GREEN, accent: "#FFFFFF", weight: 0.7, source: "https://solanapay.com" },
  { id: "seeker", style: "board", lines: ["SEEKER", "gm FROM YOUR POCKET"], bg: INK, ink: "#FFFFFF", accent: PURPLE, weight: 0.6,
    source: "Solana Mobile's phone: https://blog.solanamobile.com/post/solana-mobile-gm-emmett-hollyer-unveils-solana-seeker-at-breakpoint-2024" },
  { id: "validators", style: "board", lines: ["THANK YOU,", "VALIDATORS"], bg: "#FFFFFF", ink: PURPLE, accent: GREEN, weight: 0.6 },
  { id: "soe", style: "sheet", lines: ["SOLANA ON EXCEL", "@SolanaOnExcel"], bg: "#FFFFFF", ink: "#107C41", accent: "#107C41", weight: 1.2, source: "credit: x.com/SolanaOnExcel" },
  { id: "baltics", style: "board", lines: ["SOLANA BALTICS"], bg: INK, ink: "#FFFFFF", accent: "#F2A93B", weight: 1.2, logo: "icons/sb-mark-white.svg",
    source: "credit: Solana Baltics — the SB mark (brand/telegram/sb-mono.svg, in white), clean (Karolis 2026-10-16)" },
  { id: "foundation", style: "board", lines: ["SOLANA FOUNDATION,", "HELLO THERE :)"], bg: PURPLE, ink: "#FFFFFF", accent: GREEN, weight: 1,
    source: "Karolis 2026-10-16: a friendly wave to the Solana Foundation — words only, no Foundation logo" },
  { id: "missing", style: "board", lines: ["MISSING CAR", "IF FOUND, RETURN TO ELON"], bg: CREAM, ink: INK, accent: "#E5484D", weight: 0.6,
    source: "the game's chase (friendly parody; no brand names)" },
  { id: "dodge", style: "board", big: [0, 1], lines: ["DODGE THE ETH.", "GRAB THE SOL."], bg: GREEN, ink: INK, accent: PURPLE, weight: 0.5, source: "the game's own line (manifest)" }
];

/** Rare lines for the road (HUD toast or painted text): one per run at most, after `afterMetres`. */
export const ROAD_MESSAGES: { text: string; afterMetres: number }[] = [
  { text: "gm, RUNNER", afterMetres: 300 },
  { text: "ELON WANTS HIS CAR BACK", afterMetres: 600 },
  { text: "THIS LANE IS ONLY POSSIBLE ON SOLANA", afterMetres: 800 },
  { text: "FEW UNDERSTAND THIS LANE", afterMetres: 1200 },
  { text: "PROBABLY NOTHING AHEAD", afterMetres: 1500 },
  { text: "CHEWING GLASS AT 44 M/S", afterMetres: 2500 },
  { text: "STILL FASTER THAN A SPREADSHEET RECALC", afterMetres: 3000 },
  { text: "LFG", afterMetres: 4000 }
];

/** Loading-screen tips. Entries with `until` stop showing after that day. */
export const LOADING_TIPS: { text: string; until?: string }[] = [
  { text: "BREAKPOINT 2026 · LONDON · OLYMPIA · 15–17 NOV", until: "2026-11-17" },
  { text: "CODE BP26-VIBHU · 25% OFF BREAKPOINT 2026 TICKETS", until: "2026-11-17" },
  { text: "1 SOL = 1,000,000,000 LAMPORTS. GRAB THEM ALL." },
  { text: "SOLANA SLOTS ARE 200 MS SINCE 9 OCT 2026. SWIPE ACCORDINGLY." },
  { text: "PROOF OF HISTORY PROVES DATA EXISTED BEFORE THE PROOF. ELON CAN PROVE IT WAS HIS CAR." },
  { text: "CHEWING GLASS IS WHAT SOLANA DEVS DO. SWIPING IS WHAT YOU DO." },
  { text: "ALPENGLOW TARGETS ~150 MS FINALITY. ELON TARGETS HIS CAR." },
  { text: "SEALEVEL RUNS PROGRAMS IN PARALLEL. YOU GET THREE LANES." },
  { text: "gm. ALWAYS gm." },
  { text: "MADE BY SOLANA ON EXCEL · BUILT IN THE BALTICS BY SOLANA BALTICS" }
];

/** Crash screen: an Excel error value + a punchline (the chaser is ELON: you stole his car, he wants it back). */
export const CRASH_JOKES: string[] = [
  "#REF! — ELON GOT HIS CAR BACK",
  "#N/A — LANE NOT AVAILABLE",
  "#VALUE! — THE ETH TRUCK WON",
  "#NAME? — WHO PARKED THAT THERE",
  "#NULL! — CAR RETURNED TO ITS OWNER",
  "#SPILL! — SOL EVERYWHERE",
  "#NUM! — TOO FAST EVEN FOR 200 MS SLOTS",
  "#DIV/0! — ZERO LANES LEFT",
  "#BUSY! — EXCEL IS NOT RESPONDING. SOLANA WAS.",
  "#CALC! — CIRCULAR REFERENCE: SAME TRUCK, TWICE",
  "=IF(SWIPED, SOL, ELON)",
  "ELON GOT HIS CAR BACK. STEAL IT AGAIN?",
  "=SUM(REGRETS) · TRY AGAIN"
];

/** True when a dated entry may still show on `now` (the whole `until` day counts). */
export function inDate(until: string | undefined, now: Date = new Date()): boolean {
  if (!until) return true;
  const [y, m, d] = until.split("-").map(Number);
  return now.getTime() < new Date(y, m - 1, d + 1).getTime();
}

/** The signs that may show today. */
export function liveSigns(now: Date = new Date()): SignSpec[] {
  return SIGNS.filter((sign) => inDate(sign.until, now));
}

/** A loading tip for today (random). */
export function pickTip(random: () => number = Math.random, now: Date = new Date()): string {
  const tips = LOADING_TIPS.filter((tip) => inDate(tip.until, now));
  return tips[Math.floor(random() * tips.length)]?.text ?? "";
}

/** A crash joke (random). */
export function pickCrashJoke(random: () => number = Math.random): string {
  return CRASH_JOKES[Math.floor(random() * CRASH_JOKES.length)];
}
